// Background image preloader
// Preloads first puzzle of each collection after initial game load

interface PreloadableLevel {
  imageUrl: string;
}

interface PreloadableCollection {
  levels: PreloadableLevel[];
}

// Check WebP support once at startup
let webpSupported: boolean | null = null;

function checkWebPSupport(): Promise<boolean> {
  if (webpSupported !== null) return Promise.resolve(webpSupported);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      webpSupported = img.width > 0 && img.height > 0;
      resolve(webpSupported);
    };
    img.onerror = () => {
      webpSupported = false;
      resolve(false);
    };
    // Tiny WebP image
    img.src =
      "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=";
  });
}

// Initialize WebP check immediately
checkWebPSupport();

export function getResponsiveImageUrl(baseUrl: string): string {
  if (baseUrl.startsWith("/images/pexels-") && !baseUrl.includes(".")) {
    const width = Math.max(window.innerWidth, window.innerHeight);
    let size = "800";
    if (width > 1200) size = "1920";
    else if (width > 800) size = "1200";
    const ext = webpSupported ? "webp" : "jpeg";
    return `${baseUrl}-${size}.${ext}`;
  }
  return baseUrl;
}

export function getImageUrls(baseUrl: string): { webp: string; jpeg: string } {
  if (baseUrl.startsWith("/images/pexels-") && !baseUrl.includes(".")) {
    const width = Math.max(window.innerWidth, window.innerHeight);
    let size = "800";
    if (width > 1200) size = "1920";
    else if (width > 800) size = "1200";
    return {
      webp: `${baseUrl}-${size}.webp`,
      jpeg: `${baseUrl}-${size}.jpeg`,
    };
  }
  return { webp: baseUrl, jpeg: baseUrl };
}

class ImagePreloader {
  private preloadedUrls = new Set<string>();
  private isPreloading = false;
  private queue: string[] = [];

  // Preload just the first puzzle of each collection (for fast collection switching)
  public preloadCollectionPreviews(collections: PreloadableCollection[]): void {
    if (this.isPreloading) return;

    const urls: string[] = [];
    for (const collection of collections) {
      if (collection.levels.length > 0) {
        const url = getResponsiveImageUrl(collection.levels[0].imageUrl);
        if (!this.preloadedUrls.has(url)) {
          urls.push(url);
        }
      }
    }

    this.queue = urls;
    this.startPreloading();
  }

  private startPreloading(): void {
    if (this.isPreloading || this.queue.length === 0) return;

    this.isPreloading = true;

    this.preloadNext();
  }

  private preloadNext(): void {
    if (this.queue.length === 0) {
      this.isPreloading = false;
      return;
    }

    const url = this.queue.shift()!;

    const img = new Image();
    img.onload = () => {
      this.preloadedUrls.add(url);
      // Small delay to not overwhelm the browser
      setTimeout(() => this.preloadNext(), 50);
    };
    img.onerror = () => {
      // Skip failed images
      setTimeout(() => this.preloadNext(), 50);
    };
    img.src = url;
  }

  public isPreloaded(url: string): boolean {
    return this.preloadedUrls.has(getResponsiveImageUrl(url));
  }
}

export const imagePreloader = new ImagePreloader();
